import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  Apartment,
  ChevronRight,
  Group,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { userActions } from "../../redux/actions";
import CopyrightText from "../../components/common/CopyrightText";
import { AUTH_USER_TYPES } from "../../constants/auth.constants";
import {
  DEFAULT_TENANT_LOGIN_CONTEXT_ID,
  getLoginContextById,
  TENANT_LOGIN_CONTEXTS,
} from "../../config/loginTenants";
import PanasonicLogo from "../../assets/logo/syslogin_panasonic_logo.png";

const HERO_BACKGROUND = "/assets/panasonic-building.jpg";

export default function SignIn() {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const isLoggingIn = useSelector((state) => state?.authentication?.loading);
  const loginErrorMessage = useSelector(
    (state) =>
      state?.authentication?.error?.error ||
      (typeof state?.authentication?.error === "string"
        ? state.authentication.error
        : "") ||
      ""
  );
  const [visibleLoginError, setVisibleLoginError] = useState("");

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    if (!loginErrorMessage) {
      setVisibleLoginError("");
      return;
    }

    setVisibleLoginError(loginErrorMessage);
    const timer = setTimeout(() => setVisibleLoginError(""), 6000);
    return () => clearTimeout(timer);
  }, [loginErrorMessage]);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState({
    username: "",
    password: "",
  });
  const [loginType, setLoginType] = useState(null);
  const [loginContextId, setLoginContextId] = useState(
    DEFAULT_TENANT_LOGIN_CONTEXT_ID
  );
  const [forgotPasswordHint, setForgotPasswordHint] = useState(false);
  const [usernameFocused, setUsernameFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [usernameAutofilled, setUsernameAutofilled] = useState(false);
  const [passwordAutofilled, setPasswordAutofilled] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const usernameRef = useRef(null);
  const passwordRef = useRef(null);

  const syncAutofillValues = useCallback(() => {
    const usernameValue = usernameRef.current?.value ?? "";
    const passwordValue = passwordRef.current?.value ?? "";
    if (usernameValue) setUsername(usernameValue);
    if (passwordValue) setPassword(passwordValue);
  }, []);

  const handleAutofillAnimation = (setAutofilled) => (event) => {
    if (event.animationName === "mui-auto-fill") {
      setAutofilled(true);
      syncAutofillValues();
    } else if (event.animationName === "mui-auto-fill-cancel") {
      setAutofilled(false);
    }
  };

  useEffect(() => {
    syncAutofillValues();
    const timers = [100, 300, 600, 1000].map((delay) =>
      setTimeout(syncAutofillValues, delay)
    );
    window.addEventListener("pageshow", syncAutofillValues);
    const handleFirstInteraction = () => syncAutofillValues();
    document.addEventListener("pointerdown", handleFirstInteraction, {
      once: true,
    });

    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener("pageshow", syncAutofillValues);
      document.removeEventListener("pointerdown", handleFirstInteraction);
    };
  }, [syncAutofillValues]);

  const handleSelectLoginType = (type) => {
    setVisibleLoginError("");
    setFieldErrors({ username: "", password: "" });
    setForgotPasswordHint(false);
    if (type === AUTH_USER_TYPES.SYSTEM) {
      setLoginType(AUTH_USER_TYPES.SYSTEM);
      setLoginContextId("system");
      return;
    }
    setLoginType(AUTH_USER_TYPES.TENANT);
    setLoginContextId(DEFAULT_TENANT_LOGIN_CONTEXT_ID);
  };

  const handleBackToOptions = () => {
    setLoginType(null);
    setForgotPasswordHint(false);
    setVisibleLoginError("");
    setFieldErrors({ username: "", password: "" });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const usernameValue = (usernameRef.current?.value ?? username).trim();
    const passwordValue = passwordRef.current?.value ?? password;
    const nextErrors = {
      username: usernameValue ? "" : "Username is required.",
      password: String(passwordValue || "").trim()
        ? ""
        : "Password is required.",
    };
    setFieldErrors(nextErrors);
    if (nextErrors.username || nextErrors.password) {
      setVisibleLoginError("");
      return;
    }
    const loginContext =
      loginType === AUTH_USER_TYPES.SYSTEM
        ? getLoginContextById("system")
        : getLoginContextById(loginContextId);
    dispatch(userActions.login(usernameValue, passwordValue, loginContext));
  };

  const loginOptions = [
    {
      title: "System User",
      description:
        "Login as a system user to manage system-level resources and tenants.",
      icon: <Group />,
      iconBg: "#0868D8",
      onClick: () => handleSelectLoginType(AUTH_USER_TYPES.SYSTEM),
    },
    {
      title: "Tenant User",
      description:
        "Login as a tenant admin to manage resources for a specific tenant.",
      icon: <Apartment />,
      iconBg: "#159447",
      onClick: () => handleSelectLoginType(AUTH_USER_TYPES.TENANT),
    },
  ];

  return (
    <Box
      component="main"
      sx={{
        position: "relative",
        height: "100vh",
        width: "100%",
        overflow: "hidden",
        backgroundImage: `url(${HERO_BACKGROUND})`,
        backgroundSize: "cover",
        backgroundPosition: "center top",
        backgroundRepeat: "no-repeat",
      }}
    >
      <CssBaseline />

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: `
            linear-gradient(
              90deg,
              rgba(220, 238, 255, 0.88) 0%,
              rgba(220, 238, 255, 0.55) 34%,
              rgba(220, 238, 255, 0.20) 62%,
              rgba(255, 255, 255, 0.05) 100%
            )
          `,
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          height: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          justifyContent: { xs: "center", md: "space-between" },
          px: { xs: 2, sm: 3, md: 5, lg: 7, xl: 9 },
          py: { xs: 2.5, md: 4 },
          gap: { xs: 2, md: 4 },
        }}
      >
        {!isMobile && (
          <Box
            sx={{
              flex: "1 1 0",
              maxWidth: 560,
              minWidth: 0,
              alignSelf: "stretch",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
              pb: { md: 1, lg: 2 },
            }}
          >
            <Typography
              sx={{
                color: "#061B58",
                fontSize: { md: 40, lg: 48, xl: 52 },
                fontWeight: 700,
                lineHeight: 1.15,
              }}
            >
              IDP Admin Console
            </Typography>

            <Typography
              sx={{
                mt: 2.5,
                color: "#3E5875",
                fontSize: { md: 16, lg: 17 },
                lineHeight: 1.6,
                maxWidth: 440,
              }}
            >
              Manage identity, tenant operations, and platform-level
              administration in one place.
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            flex: "0 0 auto",
            width: "100%",
            maxWidth: 420,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              overflow: "hidden",
              borderRadius: 1.5,
              border: "1px solid #E3EBF5",
              backgroundColor: "#FFFFFF",
              boxShadow: "0 28px 70px rgba(20,55,100,0.18)",
            }}
          >
            <Box sx={{ px: { xs: 2.5, sm: 3 }, py: { xs: 3.5, sm: 4.5 } }}>
              <Box
                component="img"
                src={PanasonicLogo}
                alt="Panasonic"
                sx={{
                  display: "block",
                  mx: "auto",
                  height: { xs: 32, sm: 36 },
                  width: "auto",
                }}
              />

              <Typography
                sx={{
                  mt: 1.5,
                  textAlign: "center",
                  color: "#4A6280",
                  fontSize: { xs: 11, sm: 12 },
                  fontWeight: 700,
                  letterSpacing: 2.5,
                }}
              >
                IDP ADMIN CONSOLE
              </Typography>

              <Box
                sx={{
                  width: 40,
                  height: 3,
                  mx: "auto",
                  mt: 1.75,
                  mb: 3,
                  bgcolor: "#0869D7",
                }}
              />

              <Typography
                sx={{
                  textAlign: "center",
                  color: "#061B58",
                  fontSize: { xs: 24, sm: 28 },
                  fontWeight: 700,
                  lineHeight: 1.2,
                }}
              >
                Welcome back
              </Typography>

              <Typography
                sx={{
                  mt: 1.25,
                  textAlign: "center",
                  color: "#3E5875",
                  fontSize: { xs: 13, sm: 14 },
                  lineHeight: 1.4,
                }}
              >
                Sign in to continue to the admin portal.
              </Typography>

              {visibleLoginError ? (
                <Alert
                  severity="error"
                  sx={{ mt: 2.5 }}
                  onClose={() => setVisibleLoginError("")}
                >
                  {visibleLoginError}
                </Alert>
              ) : null}

              {!loginType ? (
                <Box sx={{ mt: 3.5 }}>
                  <Typography
                    sx={{
                      mb: 1.75,
                      color: "#4A6280",
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: 2,
                    }}
                  >
                    LOGIN OPTIONS
                  </Typography>

                  <Stack spacing={1.75}>
                    {loginOptions.map((option) => (
                      <LoginOption key={option.title} {...option} />
                    ))}
                  </Stack>
                </Box>
              ) : (
                <Box
                  component="form"
                  noValidate
                  onSubmit={handleSubmit}
                  sx={{
                    mt: 2.5,
                    "& .MuiTextField-root": {
                      my: 1,
                    },
                    "& .MuiOutlinedInput-root": {
                      height: 48,
                      fontSize: 15,
                    },
                    "& .MuiOutlinedInput-input": {
                      py: "12px",
                      height: "auto",
                      boxSizing: "border-box",
                    },
                    "& .MuiOutlinedInput-input:-webkit-autofill": {
                      WebkitBoxShadow: "0 0 0 100px #ffffff inset",
                      WebkitTextFillColor: "#061B58",
                      caretColor: "#061B58",
                      borderRadius: "inherit",
                      transition: "background-color 9999s ease-out 0s",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: 15,
                    },
                    "& .MuiInputLabel-shrink": {
                      transform: "translate(14px, -9px) scale(0.75)",
                    },
                  }}
                >
                  {loginType === AUTH_USER_TYPES.TENANT ? (
                    <TextField
                      select
                      required
                      fullWidth
                      variant="outlined"
                      id="loginAs"
                      label="Tenant"
                      value={loginContextId}
                      onChange={(e) => setLoginContextId(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    >
                      {TENANT_LOGIN_CONTEXTS.map((context) => (
                        <MenuItem key={context.id} value={context.id}>
                          {context.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : null}

                  <TextField
                    required
                    fullWidth
                    variant="outlined"
                    id="username"
                    label="Username"
                    name="username"
                    autoFocus
                    autoComplete="username"
                    inputRef={usernameRef}
                    value={username}
                    error={Boolean(fieldErrors.username)}
                    helperText={fieldErrors.username || undefined}
                    onChange={(e) => {
                      setUsername(e.target.value);
                      if (fieldErrors.username) {
                        setFieldErrors((prev) => ({ ...prev, username: "" }));
                      }
                    }}
                    onFocus={() => {
                      setUsernameFocused(true);
                      syncAutofillValues();
                    }}
                    onBlur={() => setUsernameFocused(false)}
                    inputProps={{
                      onAnimationStart:
                        handleAutofillAnimation(setUsernameAutofilled),
                    }}
                    InputLabelProps={{
                      shrink:
                        usernameFocused ||
                        username.length > 0 ||
                        usernameAutofilled,
                    }}
                  />

                  <TextField
                    required
                    fullWidth
                    variant="outlined"
                    name="password"
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    id="password"
                    autoComplete="current-password"
                    inputRef={passwordRef}
                    value={password}
                    error={Boolean(fieldErrors.password)}
                    helperText={fieldErrors.password || undefined}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (fieldErrors.password) {
                        setFieldErrors((prev) => ({ ...prev, password: "" }));
                      }
                    }}
                    onFocus={() => {
                      setPasswordFocused(true);
                      syncAutofillValues();
                    }}
                    onBlur={() => setPasswordFocused(false)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={
                              showPassword ? "Hide password" : "Show password"
                            }
                            onClick={() => setShowPassword((prev) => !prev)}
                            onMouseDown={(event) => event.preventDefault()}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{
                      onAnimationStart:
                        handleAutofillAnimation(setPasswordAutofilled),
                    }}
                    InputLabelProps={{
                      shrink:
                        passwordFocused ||
                        password.length > 0 ||
                        passwordAutofilled,
                    }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={isLoggingIn}
                    sx={{ mt: 2.5, mb: 1.5, py: 1.15, fontSize: 15, fontWeight: 600 }}
                  >
                    {isLoggingIn ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Sign In"
                    )}
                  </Button>

                  {forgotPasswordHint ? (
                    <Alert
                      severity="info"
                      sx={{ mb: 1.5 }}
                      onClose={() => setForgotPasswordHint(false)}
                    >
                      Admin forgot-password API is not confirmed in the IDP
                      collection yet. Please contact an administrator.
                    </Alert>
                  ) : null}

                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Link
                      href="#"
                      variant="body1"
                      sx={{ fontSize: 15 }}
                      onClick={(event) => {
                        event.preventDefault();
                        handleBackToOptions();
                      }}
                    >
                      Back
                    </Link>
                    <Link
                      href="#"
                      variant="body1"
                      sx={{ fontSize: 15 }}
                      onClick={(event) => {
                        event.preventDefault();
                        setForgotPasswordHint(true);
                      }}
                    >
                      Forgot password?
                    </Link>
                  </Box>
                </Box>
              )}

              <Box sx={{ mt: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <Box sx={{ flex: 1, height: "1px", bgcolor: "#D7E0EB" }} />
                  <Typography
                    sx={{
                      color: "#6B7C92",
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: 1.2,
                      whiteSpace: "nowrap",
                    }}
                  >
                    PEOPLE | TECHNOLOGY | A BETTER TOMORROW
                  </Typography>
                  <Box sx={{ flex: 1, height: "1px", bgcolor: "#D7E0EB" }} />
                </Box>

                <Box sx={{ mt: 2, textAlign: "center" }}>
                  <CopyrightText
                    color="#5A6D84"
                    sx={{
                      fontSize: 12,
                      lineHeight: 1.4,
                      whiteSpace: "nowrap",
                      "& small": { fontSize: "inherit" },
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}

const LoginOption = ({ title, description, icon, iconBg, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      width: "100%",
      minHeight: 88,
      display: "flex",
      alignItems: "center",
      p: 1.5,
      borderRadius: 1.5,
      border: "1px solid #D9E3EE",
      backgroundColor: "#FFFFFF",
      textAlign: "left",
      cursor: "pointer",
      transition:
        "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
      "&:hover": {
        transform: "translateY(-1px)",
        borderColor: "#A8C8EC",
        boxShadow: "0 10px 24px rgba(20,75,130,0.12)",
      },
      "&:focus-visible": {
        outline: "3px solid rgba(0,95,200,0.22)",
        outlineOffset: 2,
      },
    }}
  >
    <Box
      sx={{
        width: 48,
        height: 48,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "50%",
        backgroundColor: iconBg,
        color: "#FFFFFF",
      }}
    >
      {React.cloneElement(icon, { sx: { fontSize: 24 } })}
    </Box>

    <Box sx={{ flex: 1, ml: 1.75, minWidth: 0, pr: 1 }}>
      <Typography
        sx={{
          color: "#061B58",
          fontSize: 16,
          fontWeight: 700,
          lineHeight: 1.3,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          mt: 0.4,
          color: "#3E5875",
          fontSize: 13.5,
          lineHeight: 1.4,
        }}
      >
        {description}
      </Typography>
    </Box>

    <ChevronRight sx={{ color: "#5A6D84", fontSize: 22, flexShrink: 0 }} />
  </Box>
);
